import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Building2, LogOut, Shield } from 'lucide-react';
import '../styles/colors.css';
import { useAuth } from '../contexts/AuthContext';
import { useTenantContext } from '../contexts/TenantContext';
import { useCurrentTenantFeatures } from '../hooks/useFeatures';
import TenantSwitcher from './TenantSwitcher';
import ProductSwitcher from './featureLayout/ProductSwitcher';
import { entitledProducts } from './featureLayout/products';
import { UserRole } from '../types/admin';

interface FeatureLayoutProps {
  children: React.ReactNode;
  user: any;
  signOut: () => void;
}

const OVERLINE = 'px-2.5 text-[11px] font-bold uppercase tracking-[.1em] text-[#a294c4]';

/**
 * FeatureLayout: the frame for Feature homes and admin pages. A 232px ink
 * sidebar holds the Product_Switcher (entitlement-driven), admin links and the
 * signed-in user. Workspace routes render without it (see `AppFrame`).
 * Light theme only: no `dark:` classes, no theme toggle.
 * Requirements: 2.1, 2.2
 */
const FeatureLayout = ({ children, user, signOut }: FeatureLayoutProps) => {
  const location = useLocation();
  const { currentUser, isPlatformAdmin, isTenantAdmin } = useAuth();
  const { activeTenantId, activeTenantName } = useTenantContext();
  const { features: enabledFeatures, loading: featuresLoading } = useCurrentTenantFeatures();

  // Platform Admins work across tenants, so they see every product.
  const products = entitledProducts(enabledFeatures, isPlatformAdmin);

  // Derive tenant display label
  const getTenantDisplayName = () => {
    if (isPlatformAdmin) {
      return activeTenantName ?? 'No Tenant Selected';
    }
    // ADMIN/USER: tenantId is set but name hasn't resolved yet → loading
    if (activeTenantId && !activeTenantName) {
      return null; // signals loading
    }
    return activeTenantName ?? 'Unknown Tenant';
  };

  const tenantDisplayName = getTenantDisplayName();

  // Helper function to get user's display name
  const getUserDisplayName = () => {
    // Prefer name from DynamoDB (via AuthContext) as source of truth
    if (currentUser?.name) return currentUser.name;
    // Fallbacks for Cognito attributes
    if (user?.attributes?.name) return user.attributes.name;
    if (user?.attributes?.given_name && user?.attributes?.family_name) {
      return `${user.attributes.given_name} ${user.attributes.family_name}`;
    }
    if (user?.attributes?.given_name) return user.attributes.given_name;
    if (user?.signInDetails?.loginId) {
      const email = user.signInDetails.loginId;
      const localPart = email.split('@')[0];
      if (localPart.includes('.')) {
        const parts = localPart.split('.');
        return parts.map((part: string) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
      }
      return localPart.charAt(0).toUpperCase() + localPart.slice(1);
    }
    if (user?.username) return user.username;
    return 'User';
  };

  const getUserInitials = () => {
    const displayName = getUserDisplayName();
    const parts = displayName.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return displayName.charAt(0).toUpperCase();
  };

  const getRoleLabel = () => {
    if (!currentUser) return 'Document Processing User';
    switch (currentUser.role) {
      case UserRole.PLATFORM_ADMIN: return 'Platform Admin';
      case UserRole.ADMIN: return 'Tenant Admin';
      default: return 'Document Processing User';
    }
  };

  // Tenant links, shown by role
  const adminLinks = [
    ...(isPlatformAdmin ? [{ name: 'Platform Admin', href: '/platform-admin/tenants' }] : []),
    ...(isTenantAdmin ? [{ name: 'User Management', href: '/admin/users' }] : []),
  ];

  return (
    <div className="min-h-screen flex bg-[#f9fafb]">
      {/* Ink sidebar */}
      <aside className="sticky top-0 flex h-screen w-[232px] shrink-0 flex-col bg-[#1c1428] text-white">
        <div className="flex h-16 items-center px-4">
          <Link to="/" className="flex items-center" aria-label="Clairvoya home">
            <img src="/clairvoya_horizontal_white.png" alt="Clairvoya" className="h-9 w-auto" />
          </Link>
        </div>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 py-3">
          <section className="flex flex-col gap-2">
            <div className={OVERLINE}>Products</div>
            <ProductSwitcher
              products={products}
              pathname={location.pathname}
              loading={featuresLoading && !isPlatformAdmin}
            />
          </section>

          {adminLinks.length > 0 && (
            <section className="flex flex-col gap-2">
              <div className={OVERLINE}>Admin</div>
              <nav aria-label="Admin" className="flex flex-col gap-0.5">
                {adminLinks.map((link) => {
                  const active =
                    location.pathname === link.href || location.pathname.startsWith(`${link.href}/`);
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      aria-current={active ? 'page' : undefined}
                      className={`flex items-center gap-2.5 rounded-lg px-2.5 py-[9px] text-sm transition-colors ${
                        active
                          ? 'bg-white/[.08] font-bold text-white'
                          : 'text-[#c8c0dd] hover:bg-white/[.05] hover:text-white'
                      }`}
                    >
                      <Shield size={15} strokeWidth={2} aria-hidden="true" />
                      <span className="truncate">{link.name}</span>
                    </Link>
                  );
                })}
              </nav>
            </section>
          )}
        </div>

        {/* User and sign out */}
        <div className="border-t border-white/10 p-3">
          <div className="flex items-center gap-2.5 px-1.5 pb-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#834DFB] text-xs font-bold text-white">
              {getUserInitials()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-white">{getUserDisplayName()}</p>
              <p className="truncate text-xs text-[#b8afd0]">{getRoleLabel()}</p>
            </div>
          </div>
          <button
            onClick={signOut}
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-[#c8c0dd] transition-colors hover:bg-white/[.05] hover:text-white"
          >
            <LogOut size={15} strokeWidth={2} aria-hidden="true" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-end border-b border-gray-200 bg-white px-8">
          {isPlatformAdmin ? (
            <TenantSwitcher />
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Building2 size={15} strokeWidth={2} className="text-gray-500" aria-hidden="true" />
              {tenantDisplayName === null ? (
                <span className="inline-block h-4 w-24 animate-pulse rounded bg-gray-200" />
              ) : (
                <span>{tenantDisplayName}</span>
              )}
            </div>
          )}
        </header>

        <main className="flex-1 p-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
};

export default FeatureLayout;
